import { Accordion, Button, Divider, Typography } from '@scale/scaleui';
import FeedbackAccordion from 'react-scale/components/scalers/tasks/ThreadedInteraction/ContributorFeedback/components/FeedbackAccordion';
import {
  ActionTakenEnum,
  ContributorFeedback,
  belongsToStep,
} from '@scale/shared/types/chat/contributorFeedback';
import { twMerge } from 'tailwind-merge';
import { useEffect, useState, memo, useContext, useMemo } from 'react';
import { useWorkerCommentsStore } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useWorkerCommentsStore';
import { FeedbackType } from '@scale/shared/types/chat';
import FeedbackStatusDot from 'react-scale/components/scalers/tasks/ThreadedInteraction/ContributorFeedback/components/FeedbackStatusDot';
import { useContributorFeedbackImperativeApi } from 'react-scale/components/scalers/tasks/ThreadedInteraction/ContributorFeedback/stores/useContributorFeedbackStore';
import { ContributorFeedbackStepContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/ContributorFeedback/context';
import produce from 'immer';
import { JUSTIFICATION_MIN_CHARACTERS } from 'react-scale/components/scalers/tasks/ThreadedInteraction/ContributorFeedback/constants';

const SHOW_DISMISS_ALL_BUTTON_FEEDBACK_THRESHOLD = 3;
interface IssueGroupBodyProps {
  feedbackItems: ContributorFeedback;
}

const IssueGroupBody: React.FC<IssueGroupBodyProps> = ({ feedbackItems }) => {
  return (
    <div className="flex flex-col">
      {feedbackItems.map((feedbackItem, index) => (
        <div key={feedbackItem.id}>
          {index > 0 && (
            <div className="overflow-hidden">
              <Divider className="bg-neutral-100" />
            </div>
          )}
          <FeedbackAccordion feedbackItem={feedbackItem} />
        </div>
      ))}
    </div>
  );
};

interface IssueGroupHeaderProps {
  className?: string;
  title: string;
}

const IssueGroupHeader: React.FC<IssueGroupHeaderProps> = ({ className, title }) => {
  return <Typography.Paragraph3 className={className}>{title}</Typography.Paragraph3>;
};

interface IssueGroupAccordionProps {
  className?: string;
  accordionClassName?: string;
  headerTitle?: string;
  areUnaddressedFeedbackItems?: boolean;
  feedbackItems: ContributorFeedback;
  defaultExpanded?: boolean;
}

const IssueGroupAccordion: React.FC<IssueGroupAccordionProps> = ({
  className,
  accordionClassName,
  headerTitle,
  areUnaddressedFeedbackItems,
  feedbackItems,
  defaultExpanded = true,
}) => {
  const contributorFeedbackStepContext = useContext(ContributorFeedbackStepContext);
  const [expanded, setExpanded] = useState<boolean>(defaultExpanded);
  const selectedWorkerComment = useWorkerCommentsStore(state => state.selectedWorkerComment);
  const contributorFeedbackStoreInstance = useContributorFeedbackImperativeApi();

  useEffect(() => {
    if (!feedbackItems.length || feedbackItems[0].type !== FeedbackType.HumanReviewerFeedback) {
      return;
    }

    const isSelectedWorkerCommentInFeedbackItems = feedbackItems.some(
      ({ sourceFeedbackId }) => sourceFeedbackId === selectedWorkerComment,
    );

    // We want to expand the accordion if the selected worker comment is in the feedback items
    // that this accordion houses
    if (isSelectedWorkerCommentInFeedbackItems && !expanded) {
      setExpanded(true);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWorkerComment]);

  const { automatedFeedback, nonBlockingAutomatedFeedback, activeNonBlockingAutomatedFeedback } =
    useMemo(() => {
      const automatedFeedback = [];
      const nonBlockingAutomatedFeedback = [];
      const activeNonBlockingAutomatedFeedback = [];

      for (const feedbackItem of feedbackItems) {
        if (feedbackItem.type !== FeedbackType.HumanReviewerFeedback) {
          automatedFeedback.push(feedbackItem);

          if (!feedbackItem.isBlocking && !feedbackItem.isJustificationRequired) {
            nonBlockingAutomatedFeedback.push(feedbackItem);

            if (feedbackItem.action?.actionTaken !== ActionTakenEnum.Dismissed) {
              activeNonBlockingAutomatedFeedback.push(feedbackItem);
            }
          }
        }
      }

      return {
        automatedFeedback,
        nonBlockingAutomatedFeedback,
        activeNonBlockingAutomatedFeedback,
      };
    }, [feedbackItems]);

  // Expand the accordion if the number of `automatedFeedback` items change and there are feedback items
  // This will occur when new automated feedback is generated from a subsequent step submission.
  useEffect(() => {
    if (automatedFeedback.length > 0) {
      setExpanded(true);
    }
  }, [automatedFeedback.length]);

  return (
    <div className={twMerge('border-neutral-200 border-t-[0.5px]', className)}>
      {headerTitle ? (
        <Accordion
          className={twMerge(
            'w-full bg-neutral-100 shadow-none border-none rounded-none overflow-hidden',
            accordionClassName,
          )}
          contentContainerClassName="p-0"
          buttonClassName="py-1.5"
          iconClassName="ml-2"
          header={
            <div className="flex flex-row flex-grow justify-between">
              <div className="flex flex-row items-center">
                <IssueGroupHeader
                  className="text-neutral-500"
                  title={`${headerTitle} (${feedbackItems.length})`}
                />
                {/* Only show the dot if the accordion is not expanded and there are unaddressed feedback items */}
                {!expanded && areUnaddressedFeedbackItems && <FeedbackStatusDot className="ml-2" />}
              </div>

              {/* Button to dismiss all feedback for this step that is not blocking or requires justification */}
              {nonBlockingAutomatedFeedback.length >= SHOW_DISMISS_ALL_BUTTON_FEEDBACK_THRESHOLD &&
                activeNonBlockingAutomatedFeedback.length > 0 &&
                expanded && (
                  <Button
                    className="h-6"
                    variant="white"
                    onClick={e => {
                      e.stopPropagation();
                      e.preventDefault();
                      const setFeedback =
                        contributorFeedbackStoreInstance.getState().setFullFeedbackState;
                      const allFeedbackItems = contributorFeedbackStoreInstance.getState().feedback;
                      let isActiveFeedbackWithJustificationRequired = false;

                      setFeedback(
                        produce(allFeedbackItems, draft => {
                          draft.forEach(feedbackItem => {
                            if (
                              belongsToStep(
                                feedbackItem.childId,
                                contributorFeedbackStepContext?.stepWithIndexKey,
                              ) &&
                              feedbackItem.type !== FeedbackType.HumanReviewerFeedback &&
                              !feedbackItem.isBlocking
                            ) {
                              if (
                                !feedbackItem.isJustificationRequired ||
                                (feedbackItem.action?.justification &&
                                  feedbackItem.action?.justification.length >=
                                    JUSTIFICATION_MIN_CHARACTERS)
                              ) {
                                feedbackItem.action = {
                                  ...feedbackItem.action,
                                  actionTaken: ActionTakenEnum.Dismissed,
                                };
                              }

                              if (
                                feedbackItem.isJustificationRequired &&
                                !feedbackItem.action?.actionTaken
                              ) {
                                isActiveFeedbackWithJustificationRequired = true;
                              }
                            }
                          });

                          // If there are no un-dismissed feedback items that require justification, collapse the accordion
                          if (!isActiveFeedbackWithJustificationRequired) {
                            setExpanded(false);
                          }
                        }),
                      );
                    }}
                  >
                    Dismiss All
                  </Button>
                )}
            </div>
          }
          target={<IssueGroupBody feedbackItems={feedbackItems} />}
          onChange={(_e, isExpanded) => setExpanded(isExpanded)}
          expanded={expanded}
        />
      ) : (
        <IssueGroupBody feedbackItems={feedbackItems} />
      )}
    </div>
  );
};

export default memo(IssueGroupAccordion);
